/**
 * A section within the template editor.
 *
 * The meta edit panel now includes a skill selector that determines how the
 * section is synthesized during document generation:
 *  - question_based_section → shows the attached question list (default)
 *  - any other skill        → shows the skill-specific config form; questions
 *                             are hidden (they don't contribute to those skills)
 *
 * Section ordering, depends_on, and depends_on_all are also editable here.
 *
 * If the section is inherited (not owned at the current scope), saving
 * silently customizes the section first — creating a child override with
 * the inherited question memberships copied over — and then applies the
 * user's edits to that child.
 */
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Trash2, Edit2 } from 'lucide-react'
import Badge from '../ui/Badge'
import SpecEditor from './SpecEditor'
import AddSpecModal from './AddSpecModal'
import SkillConfigForm from './SkillConfigForm'
import {
  customizeSpec,
  customizeSection,
  deleteSpec,
  detachSpecFromSection,
  fetchSkills,
  listMembershipsForSection,
  updateMembership,
  updateSpec,
  updateSection,
  deleteSection,
} from './api'
import { OWNER_LEVEL_BADGE } from './types'
import type { Scope, Section, SectionSpecMembership, SkillManifest } from './types'

const DEFAULT_SKILL = 'question_based_section'

interface Props {
  section: Section
  scope: Scope
  isOwned: boolean
  siblingKeys: string[]
  onChanged: () => Promise<void>
}

export default function SectionEditor({ section, scope, isOwned, siblingKeys, onChanged }: Props) {
  const [open, setOpen] = useState(true)
  const [editingMeta, setEditingMeta] = useState(false)

  // ── Meta fields ──────────────────────────────────────────────────────────
  const [title, setTitle] = useState(section.title)
  const [description, setDescription] = useState(section.description || '')
  const [instructions, setInstructions] = useState(section.writing_agent_instructions || '')
  const [order, setOrder] = useState(section.order)

  // ── Skill fields ─────────────────────────────────────────────────────────
  const [writingSkill, setWritingSkill] = useState(section.writing_skill ?? DEFAULT_SKILL)
  const [skillConfig, setSkillConfig] = useState<Record<string, unknown>>(
    (section.skill_config as Record<string, unknown>) ?? {},
  )
  const [dependsOn, setDependsOn] = useState<string[]>(section.depends_on ?? [])
  const [dependsOnAll, setDependsOnAll] = useState<boolean>(section.depends_on_all ?? false)

  // ── Skill manifests ──────────────────────────────────────────────────────
  const [skills, setSkills] = useState<SkillManifest[]>([])
  useEffect(() => {
    fetchSkills().then(setSkills).catch(() => { /* non-fatal — UI degrades gracefully */ })
  }, [])

  const selectedManifest = skills.find((s) => s.key === writingSkill)
  const showsQuestions = selectedManifest ? selectedManifest.shows_questions : writingSkill === DEFAULT_SKILL

  // ── Question list ────────────────────────────────────────────────────────
  const [activeMembershipId, setActiveMembershipId] = useState<number | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canAutoCustomizeSection = !isOwned && scope.level !== 'system' && !!scope.ownerId

  const scopeOwnerBody = () => {
    const body: any = { owner_level: scope.level }
    if (scope.level === 'office') body.owner_office = scope.ownerId
    if (scope.level === 'program_manager') body.owner_user = scope.ownerId
    if (scope.level === 'program') body.owner_program = scope.ownerId
    return body
  }

  const ensureOwnedSectionId = async (): Promise<string> => {
    if (isOwned || !canAutoCustomizeSection) return section.id
    const child = await customizeSection(section.id, scopeOwnerBody())
    return child.id
  }

  const handleSaveMeta = async () => {
    setSaving(true)
    setError(null)
    try {
      const targetId = await ensureOwnedSectionId()
      await updateSection(targetId, {
        title,
        description: description || null,
        writing_agent_instructions: instructions || null,
        order,
        writing_skill: writingSkill || null,
        skill_config: Object.keys(skillConfig).length > 0 ? skillConfig : null,
        depends_on: dependsOn.length > 0 ? dependsOn : null,
        depends_on_all: dependsOnAll || null,
      })
      setEditingMeta(false)
      await onChanged()
    } catch (err: any) {
      setError(err.message || 'Failed to save section')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSection = async () => {
    if (!confirm(`Delete section "${section.title}"? All specs attached to it will also be removed.`)) return
    try {
      await deleteSection(section.id)
      await onChanged()
    } catch (err: any) {
      setError(err.message || 'Failed to delete section')
    }
  }

  const toggleDependsOn = (key: string) => {
    setDependsOn((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  const nextOrder = section.section_questions.reduce((m, sq) => Math.max(m, sq.order + 1), 0)
  const badge = OWNER_LEVEL_BADGE[section.owner_level]

  // Resolved skill label for the collapsed header badge
  const skillLabel = selectedManifest?.label ?? (writingSkill || DEFAULT_SKILL)

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* ── Header ── */}
      <div className="px-5 py-3 flex items-center gap-3 border-b border-border">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-muted-foreground hover:text-foreground"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">{section.title}</span>
            <Badge variant={badge.variant}>{badge.label}</Badge>
            {!isOwned && <Badge variant="pending">Inherited</Badge>}
            {section.is_removed && <Badge variant="rejected">Removed</Badge>}
            <Badge variant="default" className="font-mono text-xs">#{section.order}</Badge>
            <Badge variant="info">{skillLabel}</Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">{section.key}</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditingMeta((e) => !e)}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded"
            aria-label="Edit section"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          {isOwned && (
            <button
              type="button"
              onClick={handleDeleteSection}
              className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
              aria-label="Delete section"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Edit panel ── */}
      {editingMeta && (
        <div className="px-5 py-4 space-y-5 border-b border-border bg-muted/30">

          {/* ── Skill selection ── */}
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Skill
            </div>
            <label className="block">
              <span className="text-sm font-medium">Writing skill</span>
              <select
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm"
                value={writingSkill}
                onChange={(e) => {
                  setWritingSkill(e.target.value)
                  setSkillConfig({})
                }}
              >
                {skills.length === 0 && (
                  <option value={writingSkill}>{writingSkill}</option>
                )}
                {skills.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              {selectedManifest && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {selectedManifest.description}
                </div>
              )}
            </label>

            {/* Per-skill config form */}
            {selectedManifest && !selectedManifest.shows_questions && (
              <div className="pl-3 border-l-2 border-border space-y-3">
                <SkillConfigForm
                  manifest={selectedManifest}
                  config={skillConfig}
                  onChange={setSkillConfig}
                />
              </div>
            )}
          </div>

          {/* ── Ordering & dependencies ── */}
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Ordering
            </div>
            <div className="flex items-center gap-4">
              <label className="block flex-none">
                <span className="text-sm font-medium">Order</span>
                <input
                  type="number"
                  className="mt-1 w-24 px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm"
                  value={order}
                  min={0}
                  onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
                />
              </label>
              <label className="flex items-center gap-2 mt-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dependsOnAll}
                  onChange={(e) => setDependsOnAll(e.target.checked)}
                  className="w-4 h-4 rounded border-input"
                />
                <span className="text-sm">Run after all other sections</span>
              </label>
            </div>

            {siblingKeys.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-1.5">Depends on</div>
                <div className="flex flex-wrap gap-2">
                  {siblingKeys.map((key) => {
                    const active = dependsOn.includes(key)
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleDependsOn(key)}
                        className={[
                          'px-2.5 py-1 rounded-md text-xs font-mono border transition-colors',
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary hover:text-foreground',
                        ].join(' ')}
                      >
                        {key}
                      </button>
                    )
                  })}
                </div>
                {dependsOn.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    This section will run after: {dependsOn.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Section metadata ── */}
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Metadata
            </div>
            <label className="block">
              <span className="text-sm font-medium">Title</span>
              <input
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Description</span>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Leave empty to inherit from upstream"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Writing agent instructions</span>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground font-mono text-xs"
                rows={3}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Leave empty to inherit from upstream"
              />
            </label>
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}
          <div className="flex items-center justify-between gap-2">
            {canAutoCustomizeSection ? (
              <div className="text-xs text-muted-foreground italic">
                Saving will create a customization at this scope.
              </div>
            ) : <div />}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setEditingMeta(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMeta}
                disabled={saving}
                className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Spec list — only shown for question-based sections ── */}
      {open && showsQuestions && (
        <div className="px-5 py-4 space-y-3">
          {section.section_questions.length === 0 && (
            <div className="text-sm text-muted-foreground italic">No specs in this section.</div>
          )}
          {section.section_questions
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((m) => {
              const specOwnedAtScope = m.spec_detail.owner_level === scope.level
              const canAutoCustomizeSpec =
                !specOwnedAtScope && scope.level !== 'system' && !!scope.ownerId
              return (
                <div key={m.id}>
                  {activeMembershipId === m.id ? (
                    <SpecEditor
                      membership={m}
                      canAutoCustomize={canAutoCustomizeSpec}
                      onSaveSpec={async (patch) => {
                        let targetSpecId = m.spec_detail.id
                        if (canAutoCustomizeSpec) {
                          const child = await customizeSpec(m.spec_detail.id, scopeOwnerBody())
                          targetSpecId = child.id
                        }
                        await updateSpec(targetSpecId, patch)
                        await onChanged()
                      }}
                      onSaveMembership={async (patch) => {
                        if (!isOwned && canAutoCustomizeSection) {
                          await customizeSection(section.id, scopeOwnerBody())
                          await onChanged()
                          return
                        }
                        await updateMembership(m.id, patch)
                        await onChanged()
                      }}
                      onRemove={async () => {
                        let membershipIdToRemove = m.id
                        if (!isOwned && canAutoCustomizeSection) {
                          const child = await customizeSection(section.id, scopeOwnerBody())
                          const childMemberships = await listMembershipsForSection(child.id)
                          const match = childMemberships.find((r) => r.spec === m.spec_detail.id)
                          if (match) membershipIdToRemove = match.id
                        }
                        await detachSpecFromSection(membershipIdToRemove)
                        if (m.spec_detail.owner_level === scope.level) {
                          try { await deleteSpec(m.spec_detail.id) } catch { /* still referenced elsewhere */ }
                        }
                        await onChanged()
                      }}
                      onClose={() => setActiveMembershipId(null)}
                    />
                  ) : (
                    <SpecRow
                      membership={m}
                      isOwnedAtScope={specOwnedAtScope}
                      onEdit={() => setActiveMembershipId(m.id)}
                    />
                  )}
                </div>
              )
            })}

          <button
            type="button"
            onClick={async () => {
              if (!isOwned && canAutoCustomizeSection) {
                try {
                  await customizeSection(section.id, scopeOwnerBody())
                  await onChanged()
                  setTimeout(() => setAddOpen(true), 0)
                } catch (err: any) {
                  setError(err.message || 'Customize failed')
                }
              } else {
                setAddOpen(true)
              }
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-dashed border-border rounded-md text-muted-foreground hover:text-foreground hover:border-primary"
          >
            <Plus className="w-3.5 h-3.5" />
            Add spec
          </button>

          <AddSpecModal
            isOpen={addOpen}
            onClose={() => setAddOpen(false)}
            sectionId={section.id}
            scope={scope}
            nextOrder={nextOrder}
            excludeSpecIds={section.section_questions.map((sq) => sq.spec_detail.id)}
            onDone={onChanged}
          />
        </div>
      )}

      {/* ── Non-question skill summary (collapsed view) ── */}
      {open && !showsQuestions && !editingMeta && (
        <div className="px-5 py-4">
          <SkillSummary
            config={section.skill_config as Record<string, unknown> | null}
            manifest={selectedManifest}
            dependsOn={section.depends_on}
            dependsOnAll={section.depends_on_all}
          />
        </div>
      )}
    </div>
  )
}

function SpecRow({
  membership,
  isOwnedAtScope,
  onEdit,
}: {
  membership: SectionSpecMembership
  isOwnedAtScope: boolean
  onEdit: () => void
}) {
  const s = membership.spec_detail
  const priority = s.interview_priority
  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full text-left bg-muted/30 hover:bg-muted/60 rounded-md p-3 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{s.key}</span>
            {priority && (
              <Badge variant={priority === 'essential' ? 'success' : priority === 'standard' ? 'info' : 'default'}>
                {priority}
              </Badge>
            )}
            {s.spec_type && <Badge variant="info">{s.spec_type}</Badge>}
            {s.category && <Badge variant="warning">{s.category}</Badge>}
            {membership.required && <Badge variant="destructive">required</Badge>}
            {!isOwnedAtScope && <Badge variant="pending">inherited</Badge>}
          </div>
          <div className="mt-1 text-sm text-foreground truncate">
            {s.prompt || <span className="italic text-muted-foreground">No prompt set</span>}
          </div>
          <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-2">
            {(s.eligible_respondent_roles || []).map((r) => (
              <span key={r} className="px-1.5 py-0.5 rounded bg-background border border-border">
                {r}
              </span>
            ))}
          </div>
        </div>
        <Edit2 className="w-4 h-4 text-muted-foreground mt-1" />
      </div>
    </button>
  )
}

function SkillSummary({
  config,
  manifest,
  dependsOn,
  dependsOnAll,
}: {
  config: Record<string, unknown> | null
  manifest: SkillManifest | undefined
  dependsOn: string[] | null
  dependsOnAll: boolean | null
}) {
  const configEntries = config ? Object.entries(config).filter(([, v]) => v !== null && v !== undefined) : []
  return (
    <div className="space-y-2 text-sm">
      {manifest?.description && (
        <div className="text-muted-foreground">{manifest.description}</div>
      )}
      {configEntries.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {configEntries.map(([k, v]) => {
            const fieldLabel = manifest?.config_schema.find((f) => f.key === k)?.label ?? k
            return (
              <span key={k} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{fieldLabel}:</span> {String(v)}
              </span>
            )
          })}
        </div>
      )}
      {dependsOnAll && (
        <div className="text-xs text-muted-foreground">Runs after all other sections.</div>
      )}
      {(dependsOn ?? []).length > 0 && (
        <div className="text-xs text-muted-foreground">
          Depends on: {dependsOn!.map((k) => (
            <span key={k} className="font-mono">{k}</span>
          )).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
        </div>
      )}
    </div>
  )
}
